import React, { useState, useEffect } from 'react';
import { db, storage } from '../../../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, query, where, getDocs, orderBy, doc, updateDoc, setDoc } from 'firebase/firestore';
import './VendorCustomBuilds.css';

const VendorCustomBuilds = () => {
    const [pendingRequests, setPendingRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPendingRequests = async () => {
            try {
                console.log('Starting to fetch requests...');
                const requestsRef = collection(db, 'custom_content_requests');
                
                // Query for paid but unfulfilled requests
                const q = query(
                    requestsRef,
                    where('status', '==', 'paid'),
                    where('fulfilled', 'in', [false, null]),  // Include both false and missing fulfilled field
                    orderBy('createdAt', 'desc')
                );

                const querySnapshot = await getDocs(q);
                console.log('Total documents found:', querySnapshot.size);
                
                // Log each document's data
                querySnapshot.docs.forEach(doc => {
                    console.log('Document data:', {
                        id: doc.id,
                        status: doc.data().status,
                        fulfilled: doc.data().fulfilled,
                        ...doc.data()
                    });
                });

                const requests = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate()
                }));

                console.log('Processed requests:', requests);
                setPendingRequests(requests);
                
            } catch (error) {
                console.error('Error fetching custom requests:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPendingRequests();
    }, []);

    const handleRequestClick = (request) => {
        setSelectedRequest(request);
    };

    const handleImageUpload = async (event, requestId) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            setError(null);

            // Create a reference to the storage location
            const path = `custom_content/${requestId}/${Date.now()}_${file.name}`;
            const storageRef = ref(storage, path);

            // Upload the file
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Update the request document
            const requestRef = doc(db, 'custom_content_requests', requestId);
            await updateDoc(requestRef, {
                status: 'fulfilled',
                fulfilled: true,
                imageUrl: downloadURL,
                storagePath: path,
                fulfilledAt: new Date()
            });

            // Add to user's customLinks collection
            const request = pendingRequests.find(r => r.id === requestId);
            if (!request.userId) {
                throw new Error('User ID not found in request');
            }

            const userCustomLinksRef = collection(db, 'users', request.userId, 'customLinks');
            await setDoc(doc(userCustomLinksRef), {
                imageUrl: downloadURL,
                createdAt: new Date(),
                requestId: requestId,
                topOutfit: request.topOutfit,
                topOutfitLabel: request.topOutfitLabel,
                outfit: request.outfitLabel,
                bottomOutfit: request.outfit,
                pose: request.poseLabel,
                poseId: request.pose,
                requestNumber: request.requestNumber,
                storagePath: path,
                fulfilledAt: new Date()
            });

            // Update local state
            setPendingRequests(prev => prev.filter(r => r.id !== requestId));
            setSelectedRequest(null);

            alert('Request fulfilled successfully!');

        } catch (error) {
            console.error('Error fulfilling request:', error);
            setError(`Failed to fulfill request: ${error.message}`);
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div className="vendor-builds-container">
                <div className="vendor-builds-header">
                    <h3>Custom Content Requests</h3>
                </div>
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="vendor-builds-container">
            <div className="vendor-builds-header">
                <h3>Custom Content Requests</h3>
                <div className="request-count">{pendingRequests.length} Pending</div>
            </div>

            <div className="requests-list">
                <div className="request-list-header">
                    <span className="req-num-col">Request #</span>
                    <span className="user-col">User</span>
                    <span className="top-col">Top</span>
                    <span className="bottom-col">Bottom</span>
                    <span className="pose-col">Pose</span>
                    <span className="status-col">Status</span>
                </div>
                
                <div className="request-items">
                    {pendingRequests.length === 0 ? (
                        <div className="no-requests">No pending requests</div>
                    ) : (
                        pendingRequests.map(request => (
                            <div 
                                key={request.id} 
                                className={`request-item ${selectedRequest?.id === request.id ? 'selected' : ''}`}
                                onClick={() => handleRequestClick(request)}
                            >
                                <span className="req-num-col">{request.requestNumber || 'N/A'}</span>
                                <span className="user-col">{request.userEmail?.split('@')[0] || 'N/A'}</span>
                                <span className="top-col">{request.topOutfitLabel || 'N/A'}</span>
                                <span className="bottom-col">{request.outfitLabel || 'N/A'}</span>
                                <span className="pose-col">{request.poseLabel || 'N/A'}</span>
                                <span className="status-col">
                                    <span className={`status-badge ${request.status}`}>
                                        {request.status === 'paid' ? 'Pending Fulfillment' : request.status}
                                    </span>
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {selectedRequest && (
                <div className="fulfillment-panel">
                    <h4>Fulfill Request</h4>
                    <div className="request-details">
                        <p>Request #: {selectedRequest.requestNumber}</p>
                        <p>User: {selectedRequest.userEmail}</p>
                        <p>Top: {selectedRequest.topOutfitLabel || 'N/A'}</p>
                        <p>Bottom: {selectedRequest.outfitLabel || 'N/A'}</p>
                        <p>Pose: {selectedRequest.poseLabel || 'N/A'}</p>
                        <p>Requested: {selectedRequest.createdAt?.toLocaleDateString()}</p>
                    </div>
                    <div className="fulfillment-form">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, selectedRequest.id)}
                            disabled={uploading}
                            id={`fulfill-upload-${selectedRequest.id}`}
                            className="hidden-input"
                        />
                        <label 
                            htmlFor={`fulfill-upload-${selectedRequest.id}`} 
                            className="fulfill-button"
                        >
                            {uploading ? 'Uploading...' : 'Upload Fulfillment Image'}
                        </label>
                        {error && <p className="error-message">{error}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorCustomBuilds;
